import React, { useState, useEffect, useMemo, useRef } from 'react';
import Chart from 'chart.js/auto';
import { formatCurrency, extractMonetaryValue, getDetailProductKey, getTimeRangeLabel } from '../../utils/dataUtils';
import { useCurrency } from '../../hooks/useCurrency';
import { useTheme } from '../../hooks/useTheme';
import {
  getChartTheme,
  tooltipStyle,
  barValueAxis,
  categoryAxis,
  barDataset,
  barChartHeight,
  barValueLabels,
  BAR_LABEL_PADDING
} from './chartTheme';

const PAGE_SIZE = 20;

export const DetailedLineItemsChart = ({
  detailedLineItems,
  timeRange,
  onCategoryClick,
  accountName
}) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const [displayCount, setDisplayCount] = useState(PAGE_SIZE);
  const [showProductSelector, setShowProductSelector] = useState(false);
  const currencyKey = useCurrency();
  const themeName = useTheme();

  // Group items by product (prioritize product field over category)
  const { chartData, totalItemCount } = useMemo(() => {
    if (!detailedLineItems || detailedLineItems.length === 0) {
      return { chartData: { labels: [], values: [] }, totalItemCount: 0 };
    }

    const productSpend = {};
    let validItems = 0;

    detailedLineItems.forEach(item => {
      const amount = extractMonetaryValue(item);
      if (amount <= 0) return; // Skip zero or negative values

      const product = getDetailProductKey(item);
      productSpend[product] = (productSpend[product] || 0) + amount;
      validItems++;
    });

    // Sort products by amount (descending)
    const sortedProducts = Object.entries(productSpend)
      .sort((a, b) => b[1] - a[1]);

    const labels = sortedProducts.map(([product]) => product);
    const values = sortedProducts.map(([, amount]) => amount);

    return { chartData: { labels, values }, totalItemCount: validItems };
  }, [detailedLineItems]);

  const rangeLabel = getTimeRangeLabel(timeRange);

  // Create and update chart
  useEffect(() => {
    if (chartInstance.current && typeof chartInstance.current.destroy === 'function') {
      chartInstance.current.destroy();
    }

    if (chartData.labels.length === 0) return;

    const ctx = chartRef.current.getContext('2d');
    const theme = getChartTheme(themeName);

    // Only show the number of items specified by displayCount
    const displayLabels = chartData.labels.slice(0, displayCount);
    const displayValues = chartData.values.slice(0, displayCount);

    chartInstance.current = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: displayLabels,
        // One series, one colour: the bar length already carries the amount
        datasets: [{
          ...barDataset('Product Cost', displayValues, theme.bar),
          hoverBackgroundColor: theme.barHover,
          // Add minimum bar length to ensure all bars are clickable
          minBarLength: 15
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: 'y',
        layout: { padding: BAR_LABEL_PADDING },
        plugins: {
          tooltip: {
            ...tooltipStyle(theme),
            callbacks: {
              label: context => {
                const value = context.raw;
                return `Cost: ${formatCurrency(value)}`;
              }
            }
          },
          title: {
            // The same text is shown above the chart, so it is not drawn again
            display: false,
            text: `Showing ${displayLabels.length} of ${chartData.labels.length} products for ${rangeLabel}`
          },
          legend: {
            display: false
          }
        },
        onClick: (event, elements) => {
          if (elements && elements.length > 0) {
            const index = elements[0].index;
            const product = displayLabels[index];
            onCategoryClick(product);
          }
        },
        scales: {
          x: barValueAxis(theme),
          y: categoryAxis(25, theme)
        }
      },
      plugins: [barValueLabels(theme)]
    });

    return () => {
      if (chartInstance.current && typeof chartInstance.current.destroy === 'function') {
        chartInstance.current.destroy();
      }
    };
  }, [chartData, displayCount, onCategoryClick, rangeLabel, currencyKey, themeName]);

  // Function to load more items
  const loadMore = () => {
    setDisplayCount(prevCount => Math.min(prevCount + PAGE_SIZE, chartData.labels.length));
  };

  // Function to show all items
  const showAll = () => {
    setDisplayCount(chartData.labels.length);
  };

  // All products, alphabetically, for the keyboard-accessible selector
  const allProducts = useMemo(
    () => [...chartData.labels].sort((a, b) => String(a).localeCompare(String(b))),
    [chartData.labels]
  );

  // If no data available
  if (chartData.labels.length === 0) {
    return (
      <div className="chart-empty">
        <p>No product data available for {rangeLabel}. Try a longer time range or refresh the data.</p>
      </div>
    );
  }

  const visibleCount = Math.min(displayCount, chartData.labels.length);

  return (
    <div style={{ position: 'relative' }}>
      <div className="chart-toolbar">
        <span className="chart-hint">
          Click on any product to see detailed line items.
        </span>

        <div>
          <span className="chart-count">
            Showing {visibleCount} of {chartData.labels.length} products
            (from {totalItemCount} line items)
          </span>
          {displayCount < chartData.labels.length && (
            <>
              <button
                type="button"
                onClick={loadMore}
                className="load-more-btn"
              >
                Load More
              </button>
              <button
                type="button"
                onClick={showAll}
                className="show-all-btn"
              >
                Show All
              </button>
            </>
          )}
        </div>
      </div>

      <div className="chart-scroll">
        <div style={{ height: `${barChartHeight(visibleCount)}px` }}>
          <canvas
            ref={chartRef}
            style={{ cursor: 'pointer' }}
            className="clickable-chart"
            role="img"
            aria-label={`Bar chart of spend by product for ${accountName || 'this account'}. Use Browse All Products to open a product without the mouse.`}
          />
        </div>
      </div>

      {/* Product Selector Section */}
      <div className="product-selector">
        <button
          type="button"
          className="btn-secondary"
          onClick={() => setShowProductSelector(prev => !prev)}
          aria-expanded={showProductSelector}
          aria-controls="product-selector-container"
        >
          Browse All Products
        </button>

        {/* Container for the product selector */}
        <div id="product-selector-container">
          {showProductSelector && (
            <select
              aria-label="Select a product to view detailed items"
              defaultValue=""
              onChange={(e) => {
                if (e.target.value) {
                  onCategoryClick(e.target.value);
                }
              }}
            >
              <option value="">-- Select a product to view detailed items --</option>
              {allProducts.map(product => (
                <option key={product} value={product}>{product}</option>
              ))}
            </select>
          )}
        </div>
      </div>
    </div>
  );
};
